import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiError, api, uploadPhoto } from '../api/client.js'
import type { ObjectContext } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Notice, Spinner } from '../components/ui.js'
import { compressImage } from '../lib/camera.js'
import { formatDate, formatDateTime, todayIso } from '../lib/format.js'
import { usePortalOverview } from './Portal.js'

export const CATEGORIES: { value: string; label: string; hint: string }[] = [
  { value: 'besichtigung', label: 'Wohnungsbesichtigung', hint: 'Wunschtermin und Kontakt des Interessenten angeben.' },
  { value: 'haushaltshilfe', label: 'Unterstützung im Haushalt', hint: 'Zum Beispiel Gardinen ab- und aufhängen oder ein Regal anbringen.' },
  { value: 'reparatur', label: 'Reparatur oder Mangel', hint: 'Was ist wo defekt? Ein Foto hilft.' },
  { value: 'sonstiges', label: 'Sonstiges', hint: '' },
]

export const TICKET_STATUS: Record<string, { label: string; tone: string }> = {
  open: { label: 'eingegangen', tone: 'open' },
  in_progress: { label: 'in Bearbeitung', tone: 'in_progress' },
  waiting: { label: 'Rückfrage an Sie', tone: 'warn' },
  done: { label: 'erledigt', tone: 'done' },
  rejected: { label: 'abgelehnt', tone: 'rejected' },
}

function TicketBadge({ status }: { status: string }) {
  const s = TICKET_STATUS[status] ?? { label: status, tone: '' }
  return <span className={`badge ${s.tone}`}>{s.label}</span>
}

interface PortalTicketRow extends ObjectContext {
  id: string
  title: string
  category: string | null
  categoryLabel: string
  status: string
  requestedDate: string | null
  createdAt: string
  updatedAt: string
  taskDate: string | null
  messages: number
}

interface PortalTicketDetail extends ObjectContext {
  id: string
  title: string
  categoryLabel: string
  description: string | null
  status: string
  requestedDate: string | null
  contact: string | null
  createdAt: string
  resolutionNote: string | null
  messages: { id: string; body: string; authorName: string | null; fromCustomer: boolean; createdAt: string }[]
  task: { dueDate: string; dueTime: string | null; status: string } | null
}

export function PortalTicketsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['portal-tickets'], queryFn: () => api<PortalTicketRow[]>('/api/portal/tickets') })
  return (
    <>
      <div className="portal-title">
        <div>
          <h1>Anliegen</h1>
          <div className="muted">Besichtigungen, Hilfe im Haushalt, Reparaturen: melden und den Stand verfolgen.</div>
        </div>
        <Link className="btn" to="/portal/tickets/neu">
          <Icon name="plus" /> Anliegen melden
        </Link>
      </div>
      <section className="card">
        {isLoading ? (
          <Spinner />
        ) : !data || data.length === 0 ? (
          <EmptyState title="Noch keine Anliegen" hint="Neue Anliegen erscheinen hier mit ihrem Stand." />
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Anliegen</th>
                  <th>Wo</th>
                  <th className="nowrap">Termin</th>
                  <th>Stand</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/portal/tickets/${t.id}`} className="strong">
                        {t.title}
                      </Link>
                      <div className="muted small">
                        {t.categoryLabel}, gemeldet {formatDate(t.createdAt.slice(0, 10))}
                      </div>
                    </td>
                    <td>
                      {t.siteName}
                      {t.path.length > 0 && <div className="muted small">{t.path.join(' › ')}</div>}
                    </td>
                    <td className="nowrap">
                      {t.taskDate ? formatDate(t.taskDate) : t.requestedDate ? <span className="muted">Wunsch {formatDate(t.requestedDate)}</span> : <span className="muted">–</span>}
                    </td>
                    <td>
                      <TicketBadge status={t.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

export function PortalNewTicketPage() {
  const overview = usePortalOverview()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [siteId, setSiteId] = useState(params.get('liegenschaft') ?? '')
  const [category, setCategory] = useState('reparatur')
  const [photo, setPhoto] = useState<{ blob: Blob; previewUrl: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sites = overview.data?.sites ?? []
  const effectiveSite = siteId || (sites.length === 1 ? sites[0]!.id : '')

  const places = useQuery({
    queryKey: ['portal-site-objects', effectiveSite],
    queryFn: () => api<{ id: string; kind: string; label: string; number: string | null }[]>(`/api/portal/sites/${effectiveSite}/objects`),
    enabled: Boolean(effectiveSite),
  })

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const photoIds: string[] = []
      if (photo) photoIds.push((await uploadPhoto(photo.blob, 'issue')).id)
      return api<{ id: string }>('/api/portal/tickets', { method: 'POST', body: { ...body, photoIds } })
    },
    onSuccess: async (result) => {
      toast('Anliegen gemeldet. Sie sehen hier, wie es weitergeht.')
      await queryClient.invalidateQueries({ queryKey: ['portal-tickets'] })
      navigate(`/portal/tickets/${result.id}`, { replace: true })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Senden fehlgeschlagen.'),
  })

  const hint = CATEGORIES.find((c) => c.value === category)?.hint

  return (
    <>
      <div className="muted small" style={{ marginBottom: 6 }}>
        <Link to="/portal/tickets">Anliegen</Link> › Neu
      </div>
      <div className="portal-title">
        <div>
          <h1>Anliegen melden</h1>
          <div className="muted">Die Verwaltung meldet sich im Portal und per E-Mail.</div>
        </div>
      </div>
      <section className="card" style={{ maxWidth: 720 }}>
        <div className="card-body">
          {error && <Notice kind="error">{error}</Notice>}
          <form
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault()
              const form = new FormData(event.currentTarget)
              const objectId = String(form.get('objectId') || effectiveSite)
              if (!objectId) {
                setError('Bitte eine Liegenschaft wählen.')
                return
              }
              create.mutate({
                objectId,
                category,
                title: form.get('title'),
                description: form.get('description') || undefined,
                requestedDate: form.get('requestedDate') || undefined,
                contact: form.get('contact') || undefined,
              })
            }}
          >
            <div className="field">
              <span>Worum geht es?</span>
              <div className="choice-grid">
                {CATEGORIES.map((c) => (
                  <label key={c.value} className={`choice ${category === c.value ? 'on' : ''}`}>
                    <input type="radio" name="category" value={c.value} checked={category === c.value} onChange={() => setCategory(c.value)} />
                    {c.label}
                  </label>
                ))}
              </div>
              {hint && <div className="hint">{hint}</div>}
            </div>
            <div className="row">
              <Field label="Liegenschaft">
                <select value={effectiveSite} onChange={(e) => setSiteId(e.target.value)} required>
                  <option value="">Bitte wählen</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Wo genau" hint="Gebäude oder Wohnung, falls bekannt.">
                <select name="objectId" defaultValue="" disabled={!effectiveSite}>
                  <option value="">Gesamte Liegenschaft</option>
                  {places.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                      {p.number ? ` (${p.number})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Betreff">
              <input
                name="title"
                required
                maxLength={160}
                placeholder={category === 'besichtigung' ? 'z. B. Besichtigung Wohnung 3. OG links' : 'Kurz, worum es geht'}
              />
            </Field>
            <Field label="Beschreibung">
              <textarea name="description" rows={4} placeholder="Was soll gemacht werden? Worauf ist zu achten?" />
            </Field>
            <div className="row">
              <Field label={category === 'besichtigung' ? 'Wunschtermin' : 'Wunschtermin (freiwillig)'}>
                <input name="requestedDate" type="date" min={todayIso()} required={category === 'besichtigung'} />
              </Field>
              <Field label="Kontakt vor Ort" hint={category === 'besichtigung' ? 'Name und Telefon des Interessenten.' : 'Wer öffnet, wer ist ansprechbar?'}>
                <input name="contact" maxLength={300} required={category === 'besichtigung'} />
              </Field>
            </div>
            <Field label="Foto (freiwillig)">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return setPhoto(null)
                  const shot = await compressImage(file)
                  setPhoto({ blob: shot.blob, previewUrl: shot.previewUrl })
                }}
              />
            </Field>
            {photo && <img src={photo.previewUrl} alt="Vorschau" className="ticket-photo" />}
            <div className="actions" style={{ marginTop: 12 }}>
              <button type="submit" className="btn" disabled={create.isPending}>
                {create.isPending ? 'Wird gesendet …' : 'Anliegen senden'}
              </button>
              <Link to="/portal/tickets" className="btn secondary">
                Abbrechen
              </Link>
            </div>
          </form>
        </div>
      </section>
    </>
  )
}

export function PortalTicketPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['portal-ticket', id], queryFn: () => api<PortalTicketDetail>(`/api/portal/tickets/${id}`) })
  const reply = useMutation({
    mutationFn: (body: string) => api(`/api/portal/tickets/${id}/messages`, { method: 'POST', body: { body } }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['portal-ticket', id] })
      await queryClient.invalidateQueries({ queryKey: ['portal-tickets'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Senden fehlgeschlagen.'),
  })
  if (isLoading || !data) return <Spinner />
  const closed = data.status === 'done' || data.status === 'rejected'

  return (
    <>
      <div className="muted small" style={{ marginBottom: 6 }}>
        <Link to="/portal/tickets">Anliegen</Link> › {data.title}
      </div>
      <div className="portal-title">
        <div>
          <h1>{data.title}</h1>
          <div className="muted">
            {data.categoryLabel}, {data.siteName}
            {data.path.length > 0 ? `, ${data.path.join(' › ')}` : ''}
          </div>
        </div>
        <TicketBadge status={data.status} />
      </div>

      {data.status === 'waiting' && <Notice kind="warn">Die Verwaltung hat eine Rückfrage an Sie. Bitte unten antworten.</Notice>}
      {data.task && (
        <Notice kind={data.task.status === 'done' ? 'success' : 'info'}>
          {data.task.status === 'done' ? 'Erledigt.' : `Eingeplant für ${formatDate(data.task.dueDate)}${data.task.dueTime ? `, bis ${data.task.dueTime} Uhr` : ''}.`}
        </Notice>
      )}

      <div className="portal-ticket">
        <section className="card">
          <div className="card-head">
            <h2>Verlauf</h2>
          </div>
          <ul className="thread">
            <li className="from-customer">
              <div className="meta">Sie, {formatDateTime(data.createdAt)}</div>
              <div className="bubble">
                {data.description ?? data.title}
                {(data.requestedDate || data.contact) && (
                  <div className="muted small" style={{ marginTop: 6 }}>
                    {data.requestedDate && <>Wunschtermin {formatDate(data.requestedDate)}. </>}
                    {data.contact && <>Kontakt vor Ort: {data.contact}</>}
                  </div>
                )}
              </div>
            </li>
            {data.messages.map((m) => (
              <li key={m.id} className={m.fromCustomer ? 'from-customer' : 'from-office'}>
                <div className="meta">
                  {m.fromCustomer ? 'Sie' : (m.authorName ?? 'Verwaltung')}, {formatDateTime(m.createdAt)}
                </div>
                <div className="bubble">{m.body}</div>
              </li>
            ))}
          </ul>
          {!closed && (
            <div className="card-body">
              {error && <Notice kind="error">{error}</Notice>}
              <form
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault()
                  const form = event.currentTarget
                  const body = String(new FormData(form).get('body') ?? '').trim()
                  if (!body) return
                  reply.mutate(body, { onSuccess: () => form.reset() })
                }}
              >
                <Field label={data.status === 'waiting' ? 'Ihre Antwort' : 'Nachricht an die Verwaltung'}>
                  <textarea name="body" rows={3} required />
                </Field>
                <button type="submit" className="btn" disabled={reply.isPending}>
                  Senden
                </button>
              </form>
            </div>
          )}
        </section>
      </div>
    </>
  )
}
